"use client";

import { CircleUser } from "lucide-react";
import { type ReactElement } from "react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "../..";
import { useTranslations, useLocale } from "use-intl";
import { PortalLocaleSwitcher } from "./locale-switcher";
import { userNavItems, getUser } from "../../services";
import type { Locale } from "../../i18n/config.ts";

function PortalHeaderUserMenu(): ReactElement | null {
  const t = useTranslations("global.UserMenu");
  const locale: Locale = useLocale() as Locale;
  const user = getUser();

  if (user?.email) {
    return (
      <TooltipProvider>
        <Tooltip>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <TooltipTrigger asChild>
                <Button
                  className="size-8 rounded-full"
                  size="icon"
                  variant="outline"
                  aria-label={t("tooltip")}
                >
                  <CircleUser strokeWidth={1} size={16} />
                </Button>
              </TooltipTrigger>
            </DropdownMenuTrigger>
            <TooltipContent>{t("tooltip")}</TooltipContent>
            <DropdownMenuContent align="end" className="w-56 font-inter">
              <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
              <DropdownMenuGroup>
                {userNavItems.map((item) => {
                  return (
                    <DropdownMenuItem key={item.id} asChild>
                      <a href={item.url}>{item.title[locale]}</a>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>{t("settings")}</DropdownMenuLabel>
              <DropdownMenuGroup>
                <PortalLocaleSwitcher />
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Button
                  size="sm"
                  className="w-full rounded-lg text-primary-foreground"
                  asChild
                >
                  <a href="https://data.oreme.org/login/logout">
                    {t("logout")}
                  </a>
                </Button>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="outline"
                aria-label={t("tooltip")}
                className="size-8 rounded-full"
              >
                <CircleUser strokeWidth={1} size={16} />
              </Button>
            </TooltipTrigger>
          </DropdownMenuTrigger>
          <TooltipContent>{t("tooltip")}</TooltipContent>
          <DropdownMenuContent align="end" className="w-56 font-inter">
            <DropdownMenuItem className="p-0 m-2 focus:text-primary-foreground">
              <Button
                size="sm"
                className="w-full rounded-lg text-primary-foreground"
                asChild
              >
                <a href="https://data.oreme.org/login">{t("login")}</a>
              </Button>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t("settings")}</DropdownMenuLabel>
            <DropdownMenuGroup>
              <PortalLocaleSwitcher />
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </Tooltip>
    </TooltipProvider>
  );
}

export { PortalHeaderUserMenu };
